import { useEffect, useState } from "react";
import { api, fmtBRL } from "@/lib/api";
import { Server, Users, Receipt, LifeBuoy, DollarSign, ArrowUpRight, Cloud, CalendarDays } from "lucide-react";

const MONTHS = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [monthlyRevenue, setMonthlyRevenue] = useState(null);
  const [monthlyRevenueError, setMonthlyRevenueError] = useState(false);
  const [selectedRevenueYear, setSelectedRevenueYear] = useState(null);
  const [recentClients, setRecentClients] = useState([]);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [portal, setPortal] = useState(null);

  useEffect(() => {
    let disposed = false;
    const refreshStats = () => {
      if (document.visibilityState !== "visible") return;
      api.get("/admin/stats")
        .then((r) => {
          if (!disposed) setStats(r.data);
        })
        .catch(() => {});
      api.get("/admin/revenue/monthly")
        .then((r) => {
          if (!disposed) {
            setMonthlyRevenue(r.data);
            setMonthlyRevenueError(false);
          }
        })
        .catch(() => {
          if (!disposed) setMonthlyRevenueError(true);
        });
    };

    refreshStats();
    const intervalId = window.setInterval(refreshStats, 30_000);
    document.addEventListener("visibilitychange", refreshStats);
    window.addEventListener("focus", refreshStats);

    api.get("/admin/clients").then((r) => setRecentClients(r.data.slice(0, 5)));
    api.get("/admin/invoices").then((r) => setRecentInvoices(r.data.slice(0, 6)));
    api.get("/admin/portal-status").then((r) => setPortal(r.data)).catch(() => setPortal(null));

    return () => {
      disposed = true;
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", refreshStats);
      window.removeEventListener("focus", refreshStats);
    };
  }, []);

  const cards = [
    { label: "Clientes ativos", value: stats?.total_clients ?? "—", icon: Users, tid: "kpi-clients" },
    { label: "VPS provisionadas", value: `${stats?.running_vps ?? "—"}/${stats?.total_vps ?? "—"}`, icon: Server, tid: "kpi-vps" },
    { label: "Receita recebida", value: stats ? fmtBRL(stats.revenue_paid) : "—", icon: DollarSign, tid: "kpi-mrr" },
    { label: "Tickets abertos", value: stats?.open_tickets ?? "—", icon: LifeBuoy, tid: "kpi-tickets" },
    { label: "Em cobrança", value: stats ? fmtBRL(stats.revenue_pending) : "—", icon: Receipt, tid: "kpi-pending" },
  ];
  const currentRevenueYear = monthlyRevenue?.current_year ?? new Date().getFullYear();
  const revenueYears = [...new Set([
    currentRevenueYear,
    ...(monthlyRevenue?.available_years ?? []),
  ])].sort((a, b) => b - a);
  const revenueYear = selectedRevenueYear ?? currentRevenueYear;
  const monthTotals = MONTHS.map((label, index) => {
    const month = index + 1;
    const result = monthlyRevenue?.months?.find(
      (item) => item.year === revenueYear && item.month === month,
    );
    return {
      label,
      month,
      amount: Number(result?.amount_brl || 0),
      invoiceCount: Number(result?.invoice_count || 0),
    };
  });
  const selectedYearTotal = monthTotals.reduce((sum, month) => sum + month.amount, 0);

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div>
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-2">// admin · painel</div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tighter">Operações Flix VPS</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-white/10 border border-white/10">
        {cards.map((c) => (
          <div key={c.label} className="bg-[#121214] p-5" data-testid={c.tid}>
            <div className="flex items-center justify-between text-zinc-500">
              <c.icon className="h-4 w-4" />
              <ArrowUpRight className="h-3 w-3" />
            </div>
            <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mt-3">{c.label}</div>
            <div className="font-mono text-3xl font-bold mt-2">{c.value}</div>
          </div>
        ))}
      </div>

      <section
        className="border border-white/10 bg-[#121214]"
        data-testid="admin-monthly-revenue"
      >
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-white/10 p-5">
          <div>
            <div className="flex items-center gap-2 font-heading font-bold">
              <CalendarDays className="h-4 w-4 text-zinc-400" />
              Faturamento recebido por mês
            </div>
            <div className="mt-1 text-xs text-zinc-500">
              Valores agrupados pela data de pagamento
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-zinc-500">
            Ano
            <select
              value={revenueYear}
              onChange={(event) => setSelectedRevenueYear(Number(event.target.value))}
              className="rounded-sm border border-white/15 bg-[#09090B] px-3 py-2 text-sm text-white outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
              data-testid="admin-monthly-revenue-year"
              aria-label="Ano do faturamento"
            >
              {revenueYears.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </label>
        </div>

        {monthlyRevenueError && !monthlyRevenue ? (
          <div className="p-6 text-sm text-red-300" role="status" data-testid="admin-monthly-revenue-error">
            Não foi possível carregar o faturamento mensal.
          </div>
        ) : !monthlyRevenue ? (
          <div className="p-6 text-sm font-mono text-zinc-500" role="status">
            Carregando faturamento mensal…
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-px bg-white/10 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
              {monthTotals.map((month) => (
                <div
                  key={month.month}
                  className="bg-[#121214] p-4"
                  data-testid={`admin-monthly-revenue-month-${month.month}`}
                >
                  <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">
                    {month.label}
                  </div>
                  <div className="mt-2 font-mono text-xl font-bold text-white">
                    {fmtBRL(month.amount)}
                  </div>
                  <div className="mt-1 text-xs text-zinc-500">
                    {month.invoiceCount} {month.invoiceCount === 1 ? "fatura paga" : "faturas pagas"}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-white/10 px-5 py-4">
              <span className="text-xs font-mono uppercase tracking-widest text-zinc-500">
                Total de {revenueYear}
              </span>
              <span className="font-mono text-lg font-bold text-emerald-300">
                {fmtBRL(selectedYearTotal)}
              </span>
            </div>
            {monthlyRevenueError && (
              <div className="border-t border-white/10 px-5 py-3 text-xs text-amber-300" role="status">
                Não foi possível atualizar os valores. Exibindo os últimos dados carregados.
              </div>
            )}
          </>
        )}
      </section>

      {/* Flix VPS Portal status */}
      {portal && (
        <div
          className={`border p-5 rounded-sm ${
            portal.connected ? "border-emerald-500/30 bg-emerald-500/5" :
            portal.configured ? "border-red-500/30 bg-red-500/5" :
            "border-white/10 bg-[#121214]"
          }`}
          data-testid="portal-status-card"
        >
          <div className="flex items-center gap-4">
            <Cloud className={`h-8 w-8 ${portal.connected ? "text-emerald-400" : portal.configured ? "text-red-400" : "text-zinc-500"}`} strokeWidth={1.5} />
            <div className="flex-1">
              <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Portal Flix VPS · {portal.base_url}</div>
              <div className="font-medium">{portal.message}</div>
            </div>
            <span className={`px-3 py-1 border rounded-sm font-mono text-xs uppercase tracking-widest ${
              portal.connected ? "border-emerald-500/40 text-emerald-400" :
              portal.configured ? "border-red-500/40 text-red-400" :
              "border-white/10 text-zinc-400"
            }`}>
              {portal.connected ? "● Conectado" : portal.configured ? "! Falha" : "◐ Mock ativo"}
            </span>
          </div>
          {!portal.configured && (
            <div className="mt-4 border-t border-white/10 pt-4 text-xs text-zinc-500 font-mono space-y-1">
              <div className="text-white uppercase tracking-widest text-[10px] mb-2">Como conectar:</div>
              <div>1. Acesse <a href="https://servers.mywebsitepanel.com" target="_blank" rel="noopener noreferrer" className="text-white underline">servers.mywebsitepanel.com</a> e faça login.</div>
              <div>2. Vá em <span className="text-white">Operations Console → Developer API → Manage API keys</span>.</div>
              <div>3. Crie uma chave (copie o token — só é mostrado uma vez).</div>
              <div>4. Cole em <span className="text-white">FLIX_TOKEN="..."</span> no arquivo <span className="text-white">/app/backend/.env</span>.</div>
              <div>5. Reinicie o backend: <span className="text-white">sudo supervisorctl restart backend</span>.</div>
            </div>
          )}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        <section className="border border-white/10 bg-[#121214]">
          <div className="p-5 border-b border-white/10 font-heading font-bold">Clientes recentes</div>
          <ul className="divide-y divide-white/10">
            {recentClients.map((c) => (
              <li key={c.user_id} className="p-4 flex items-center justify-between text-sm">
                <div>
                  <div className="font-medium">{c.name}</div>
                  <div className="text-xs text-zinc-500 font-mono">{c.email}</div>
                </div>
                <span className="text-xs font-mono text-zinc-500">{c.auth_provider}</span>
              </li>
            ))}
            {recentClients.length === 0 && <li className="p-6 text-zinc-500 text-sm">Sem clientes ainda.</li>}
          </ul>
        </section>

        <section className="border border-white/10 bg-[#121214]">
          <div className="p-5 border-b border-white/10 font-heading font-bold">Últimas faturas</div>
          <ul className="divide-y divide-white/10">
            {recentInvoices.map((i) => (
              <li key={i.invoice_id} className="p-4 flex items-center justify-between text-sm">
                <div>
                  <div className="font-mono">{i.number}</div>
                  <div className="text-xs text-zinc-500">{i.plan_name}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono">{fmtBRL(i.amount_brl)}</div>
                  <div className={`text-xs font-mono uppercase ${i.status === "paid" ? "text-emerald-400" : "text-amber-400"}`}>{i.status}</div>
                </div>
              </li>
            ))}
            {recentInvoices.length === 0 && <li className="p-6 text-zinc-500 text-sm">Sem faturas.</li>}
          </ul>
        </section>
      </div>
    </div>
  );
}
